
import type {
  Config,
  ConfigSchema,
  ObjectConfigFieldSchema,
} from '@empathyco/playboard-core-domain'
import type { Location, Route } from 'vue-router'
import type {
  GuardResolveFunction,
  InstanceManagementNavItem,
  InstanceManagementNavItemChild,
} from '../../models'

import { ConfigFieldVisibilityEnum } from '@empathyco/playboard-core-domain'

import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import InstanceManagementNavbar from '../../components/instance-management/instance-management-navbar.component.vue'
import InstanceTag from '../../components/instance-management/instance-tag.component.vue'

import { FeatureIdEnum, InstanceServiceIdEnum } from '../../enums'

import { ROUTER_CONSTS } from '../../router/router.consts'

import { CONFIG_CONSTS } from '../../store/config/config.consts'

import { INSTANCE_MANAGEMENT_CONSTS } from '../../store/instance-management/instance-management.consts'

const SEARCH_ID = 'search'
const INDEX_ID = 'index'

export default defineComponent({
  name: 'InstanceManagementForm',
  components: {
    InstanceTag,
    InstanceManagementNavbar,
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      instanceConfig: CONFIG_CONSTS.getters.currentInstance,
    }),
    ...mapGetters(INSTANCE_MANAGEMENT_CONSTS.moduleName, {
      serviceConfigs: INSTANCE_MANAGEMENT_CONSTS.getters.serviceConfigs,
    }),
    service(): string {
      return this.getServiceFromRoute(this.$route)
    },
    child(): string {
      return this.$route.params.child
    },
    navItems(): InstanceManagementNavItem[] {
      if (!this.configLoaded) {
        return []
      }
      const sortedServiceIds: string[] = Object.values(InstanceServiceIdEnum)
      const navItems = this.serviceConfigs.reduce(
        (items: InstanceManagementNavItem[], serviceConfig: Config) => {
          if (
            serviceConfig.schema.front_metadata?.visibility !== ConfigFieldVisibilityEnum.HIDDEN
          ) {
            items.push({
              id: serviceConfig.service,
              label: this.getServiceLabel(serviceConfig.service, serviceConfig.schema),
              isBeta: !!serviceConfig.schema.front_metadata?.beta,
              enabled: serviceConfig.enabled,
              to: this.getServiceLocation(serviceConfig.service, this.$route),
              children: this.getNavChildren(serviceConfig),
            })
          }
          return items
        },
        [],
      )
      navItems.sort(
        ({ id: id1 }: InstanceManagementNavItem, { id: id2 }: InstanceManagementNavItem) => {
          const indexOfService1 = sortedServiceIds.indexOf(id1)
          const indexOfService2 = sortedServiceIds.indexOf(id2)
          if (indexOfService1 !== indexOfService2) {
            return indexOfService1 - indexOfService2
          }
          return id1.localeCompare(id2)
        },
      )
      navItems.push({
        id: SEARCH_ID,
        label: this.getServiceLabel(SEARCH_ID),
        enabled: true,
        to: this.getServiceLocation(SEARCH_ID, this.$route),
      })
      navItems.unshift({
        id: INDEX_ID,
        label: this.getServiceLabel(INDEX_ID),
        enabled: true,
        to: this.getServiceLocation(INDEX_ID, this.$route),
      })
      return navItems
    },
  },
  data() {
    return {
      baseClass: 'epb-instance-management-form',
      configLoaded: false,
      messages: this.$epb.messages.instanceManagement,
    }
  },
  methods: {
    ...mapActions(INSTANCE_MANAGEMENT_CONSTS.moduleName, {
      listServiceConfigs: INSTANCE_MANAGEMENT_CONSTS.actions.listServiceConfigs,
      readSearchServiceConfig: INSTANCE_MANAGEMENT_CONSTS.actions.readSearchServiceConfig,
    }),
    async loadServiceConfigs(): Promise<void> {
      try {
        await this.listServiceConfigs({ instance: this.instanceConfig.id })
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      }
    },
    async loadSearchServiceConfig(): Promise<void> {
      try {
        await this.readSearchServiceConfig(this.instanceConfig.id)
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      }
    },
    getServiceFromRoute(route: Route): string {
      let service: string
      if (route.name === ROUTER_CONSTS.routes[FeatureIdEnum.INSTANCE_MANAGEMENT].root) {
        return
      }
      service = SEARCH_ID
      if (route.name === ROUTER_CONSTS.routes[FeatureIdEnum.INSTANCE_MANAGEMENT].search) {
        return service
      }
      service = INDEX_ID
      if (route.name === ROUTER_CONSTS.routes[FeatureIdEnum.INSTANCE_MANAGEMENT].index) {
        return service
      }
      service = route.params[ROUTER_CONSTS.params.service]
      if (this.serviceConfigs.some((serviceConfig: Config) => serviceConfig.service === service)) {
        return service
      }
    },
    getServiceLabel(service: string, schema?: ConfigSchema): string {
      return (
        this.messages.serviceConfig.services[service]?.label ??
        schema?.front_metadata?.display_name ??
        service
      )
    },
    getChildLabel(service: string, child: string, schema?: ConfigSchema): string {
      return (
        this.messages.serviceConfig.services[service]?.fields?.[child]?.label ||
        (schema as ObjectConfigFieldSchema).properties[child].front_metadata?.display_name ||
        child
      )
    },
    getServiceLocation(service: string, route?: Route): Location {
      let name: string
      const routeNames = ROUTER_CONSTS.routes[FeatureIdEnum.INSTANCE_MANAGEMENT]
      switch (service) {
        case SEARCH_ID:
          name = routeNames.search
          break
        case INDEX_ID:
          name = routeNames.index
          break
        default:
          name = routeNames.service
          break
      }
      const params = service !== SEARCH_ID &&
        service !== INDEX_ID && {
          [ROUTER_CONSTS.params.service]: service,
          [ROUTER_CONSTS.params.child]: undefined,
        }
      return {
        ...route,
        name,
        params: {
          ...route?.params,
          ...params,
        },
      }
    },
    getNavChildren(service: Config): InstanceManagementNavItemChild[] {
      if (service.service === InstanceServiceIdEnum.PLAYBOARD) {
        return Object.keys((service.schema as ObjectConfigFieldSchema).properties).map(
          (child: string): InstanceManagementNavItemChild => ({
            id: child,
            label: this.getChildLabel(service.service, child, service.schema),
            to: this.getChildLocation(service.service, child),
          }),
        )
      }
    },
    getChildLocation(service: string, child: string): Location {
      const serviceLocation = this.getServiceLocation(service, this.$route)
      return {
        ...serviceLocation,
        params: {
          ...serviceLocation.params,
          [ROUTER_CONSTS.params.child]: child,
        },
      }
    },
  },
  watch: {
    navItems: {
      handler(navItems: InstanceManagementNavItem[]): void {
        if (!this.service && !!navItems.length) {
          this.$router.replace(navItems[0].to)
        }
      },
      immediate: true,
    },
  },
  async created(): Promise<void> {
    this.$emit('updateLoading', { visible: true })
    try {
      await this.loadServiceConfigs()
      await this.loadSearchServiceConfig()
      this.configLoaded = true
    } catch (error) {
    } finally {
      this.$emit('updateLoading', { visible: false })
    }
  },
  beforeRouteUpdate(to: Route, from: Route, next: GuardResolveFunction): void {
    let redirectLocation: Location
    const service = this.getServiceFromRoute(to)
    if (!service && !!this.navItems.length) {
      redirectLocation = this.navItems[0].to
    }
    next(redirectLocation)
  },
})
